/**
 * Frontend mirror of backend/src/products/variant-units.ts.
 * Keep both in sync. Drives the admin variant editor's price preview,
 * conversion factor, and reservation validation.
 */

// Base product unitType → { dimension, canonical size in sub-units }.
const BASE_CANONICAL = {
  '100 Grams': { dimension: 'weight', size: 100 },
  '250 Grams': { dimension: 'weight', size: 250 },
  '500 Grams': { dimension: 'weight', size: 500 },
  '750 Grams': { dimension: 'weight', size: 750 },
  '1 Kg': { dimension: 'weight', size: 1000 },
  Piece: { dimension: 'count', size: 1 },
  Heads: { dimension: 'count', size: 1 },
  Pair: { dimension: 'count', size: 2 },
};

export const WEIGHT_UNITS = ['g', 'Kg'];
export const COUNT_UNITS = ['piece', 'head'];

/**
 * Whether a unit type participates in the variant conversion system.
 * Only the built-in types have a canonical size; admin-added custom unit types
 * (BMF-87) are label-only, so the product form hides the variant editor for them.
 * Mirrors backend isConvertibleUnit().
 */
export function isConvertibleUnit(baseUnitType) {
  return Object.prototype.hasOwnProperty.call(BASE_CANONICAL, baseUnitType);
}

export function dimensionOf(baseUnitType) {
  return BASE_CANONICAL[baseUnitType]?.dimension ?? 'weight';
}

export function baseCanonicalSize(baseUnitType) {
  return BASE_CANONICAL[baseUnitType]?.size ?? 0;
}

/** Variant units valid for a given base product (enforces same-dimension). */
export function variantUnitsFor(baseUnitType) {
  return dimensionOf(baseUnitType) === 'count' ? COUNT_UNITS : WEIGHT_UNITS;
}

export function variantDimensionOfUnit(unit) {
  if (WEIGHT_UNITS.includes(unit)) return 'weight';
  if (COUNT_UNITS.includes(unit)) return 'count';
  return null;
}

export function variantCanonicalSize(weight, unit) {
  const w = Number(weight) || 0;
  switch (unit) {
    case 'g': return w;
    case 'Kg': return w * 1000;
    case 'piece':
    case 'head': return w;
    default: return 0;
  }
}

/** How many base-units one variant-unit consumes. 0 if dimensions don't match. */
export function conversionFactor(baseUnitType, weight, unit) {
  if (dimensionOf(baseUnitType) !== variantDimensionOfUnit(unit)) return 0;
  const base = baseCanonicalSize(baseUnitType);
  if (!base) return 0;
  return variantCanonicalSize(weight, unit) / base;
}

export function composeLabel(weight, unit) {
  const pretty = { g: 'g', Kg: 'Kg', piece: 'Piece', head: 'Head' };
  const w = String(Number(weight) || 0);
  const label = pretty[unit] ?? unit;
  if ((unit === 'piece' || unit === 'head') && Number(weight) > 1) return `${w} ${label}s`;
  return `${w} ${label}`;
}

/** Suggested variant price = basePrice × conversion factor (rounded to paise). */
export function computeVariantPrice(basePrice, baseUnitType, weight, unit) {
  const f = conversionFactor(baseUnitType, weight, unit);
  if (!f) return null;
  return Math.round(Number(basePrice) * f * 100) / 100;
}

/**
 * Flash price for an arbitrary unit price (base OR variant) given the deal's
 * percentage. Mirrors backend/src/flash-deals/flash-pricing.ts so a discounted
 * variant shows the same number the server will charge.
 */
export function applyFlashDiscount(unitPrice, discountPercent) {
  // Whole rupees — the backend rounds flash prices to integers, so this must too,
  // otherwise the card shows a decimal (e.g. ₹24.50) the cart then charges as ₹25.
  return Math.round(Number(unitPrice) * (1 - Number(discountPercent) / 100));
}
